import React, { useState } from 'react'

import { useMutation } from '@apollo/client'

import { client } from '../../../../apollo'
import { UPGRADE_TO_SSO } from '../../../../graphql/enterprisePlan'

type FormState = {
  enterprisePlanId: number | null
  orgUrl: string
  slug: string
  scope: string[]
  stagingOrgUrl: string
  additionalDomains: string[]
}

export default function UpgradeToSSOForm() {
  const [formData, setFormData] = useState<FormState>({
    enterprisePlanId: 1,
    orgUrl: '',
    slug: '',
    scope: ['email', 'openid'],
    stagingOrgUrl: 'https://integrator-4546131.okta.com', // Default to dev Okta tenant
    additionalDomains: [''],
  })

  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<boolean>(false)
  const [submittedData, setSubmittedData] = useState<FormState | null>(null)
  const [enterprisePlanId, setEnterprisePlanId] = useState<number | null>(null)

  const [upgradeToSSOEnterprise, { loading }] = useMutation(UPGRADE_TO_SSO, {
    onCompleted: (data) => {
      const result = data.adminUpgradeToSsoEnterprisePlan
      if (result.enterprisePlanId) {
        setSuccess(true)
        setEnterprisePlanId(result.enterprisePlanId)
        setSubmittedData({ ...formData })
      }
    },
    onError: (err) => {
      setError(err.message || 'Failed to upgrade to SSO enterprise plan')
    },
    client,
  })

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target

    let parsedValue: string | number = value
    if (name === 'enterprisePlanId' && value) {
      parsedValue = parseInt(value, 10)
    }

    setFormData({
      ...formData,
      [name]: parsedValue,
    })
  }

  const handleDomainChange = (index: number, value: string) => {
    const updatedDomains = [...formData.additionalDomains]
    updatedDomains[index] = value
    setFormData({
      ...formData,
      additionalDomains: updatedDomains,
    })
  }

  const addDomainField = () => {
    setFormData({
      ...formData,
      additionalDomains: [...formData.additionalDomains, ''],
    })
  }

  const removeDomainField = (index: number) => {
    const updatedDomains = [...formData.additionalDomains]
    updatedDomains.splice(index, 1)
    setFormData({
      ...formData,
      additionalDomains: updatedDomains.length > 0 ? updatedDomains : [''],
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)
    setSubmittedData(null)

    if (!formData.enterprisePlanId) {
      setError('Please select an enterprise plan to upgrade')
      return
    }

    // Filter out empty domains
    const filteredDomains = formData.additionalDomains.filter(
      (domain) => domain.trim() !== '',
    )

    try {
      await upgradeToSSOEnterprise({
        variables: {
          enterprisePlanId: formData.enterprisePlanId,
          orgUrl: formData.orgUrl,
          slug: formData.slug,
          scope: formData.scope,
          stagingOrgUrl: formData.stagingOrgUrl,
          additionalDomains:
            filteredDomains.length > 0 ? filteredDomains : undefined,
        },
      })
    } catch (_err: any) {
      // Error handling is done in onError callback
    }
  }

  return (
    <div>
      {success && submittedData && (
        <div>
          <h3>Enterprise plan successfully upgraded to SSO!</h3>
          <p>Enterprise Plan ID: {enterprisePlanId}</p>

          <h4>SSO Configuration:</h4>
          <div>
            <div>
              <strong>Organization URL:</strong> {submittedData.orgUrl}
            </div>
            <div>
              <strong>URL Slug:</strong> {submittedData.slug}
            </div>
            {submittedData.stagingOrgUrl && (
              <div>
                <strong>Staging Organization URL:</strong>{' '}
                {submittedData.stagingOrgUrl}
              </div>
            )}
          </div>
        </div>
      )}

      {error && <div>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div>
          <label htmlFor="enterprisePlanId">
            Enterprise Plan to Upgrade
            <input
              type="number"
              id="enterprisePlanId"
              name="enterprisePlanId"
              placeholder="Enter Enterprise Plan ID"
              value={formData.enterprisePlanId || ''}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div>
          <label htmlFor="orgUrl">
            Organization URL
            <input
              type="text"
              id="orgUrl"
              name="orgUrl"
              placeholder="https://example.okta.com"
              value={formData.orgUrl}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div>
          <label htmlFor="slug">
            URL Slug (for OAuth endpoints)
            <input
              type="text"
              id="slug"
              name="slug"
              placeholder="okta-example"
              value={formData.slug}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div>
          <label htmlFor="scope-y">
            Scope &nbsp;
            <input
              type="text"
              id="scope-y"
              name="scope-y"
              value={formData.scope.join(',')}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  scope: e.target.value.split(','),
                }))
              }
              required
            />
          </label>
          &nbsp; comma-separated (current:{' '}
          {`[${formData.scope.map((x) => `"${x}"`).join(', ')}]`})
        </div>

        <div>
          <label htmlFor="stagingOrgUrl">
            Staging Organization URL
            <input
              type="text"
              id="stagingOrgUrl"
              name="stagingOrgUrl"
              placeholder="https://example-staging.okta.com"
              value={formData.stagingOrgUrl}
              onChange={handleInputChange}
            />
          </label>
        </div>

        <div>
          <div>
            <span id="additional-domains-label">
              Additional Domains (Optional)
            </span>
            {formData.additionalDomains.map((domain, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`domain-${index}`}>
                <label htmlFor={`domain-${index}`}>
                  <span>Domain {index + 1}</span>
                  <input
                    type="text"
                    id={`domain-${index}`}
                    value={domain}
                    onChange={(e) => handleDomainChange(index, e.target.value)}
                    placeholder="example.com"
                    aria-labelledby="additional-domains-label"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => removeDomainField(index)}
                  disabled={
                    formData.additionalDomains.length === 1 && index === 0
                  }
                >
                  Remove
                </button>
              </div>
            ))}
            <button type="button" onClick={addDomainField}>
              Add Domain
            </button>
          </div>
        </div>

        <button type="submit" disabled={loading}>
          {loading ? 'Upgrading...' : 'Upgrade to SSO Enterprise Plan'}
        </button>
      </form>
    </div>
  )
}
